import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, signal, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ResponsiveService } from '@foxera/shared/services/responsive.service';
import { FoxeraDrawerFullComponent } from '@foxera/shared/components/foxera-drawer-full/foxera-drawer-full.component';

// Interfaces
import { IExerciseFilterConstants } from '@foxera/shared/interfaces/client.interface';
import { IExerciseFilters } from '@foxera/feature/clients/interfaces/filter-exercice.interface';

@Component({
  selector: 'app-exercise-filter-dialog',
  standalone: true,
  templateUrl: './exercise-filter-dialog.component.html',
  styleUrls: ['./exercise-filter-dialog.component.css'],
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    FormsModule,
    CheckboxModule,
    RadioButtonModule,
    FoxeraDrawerFullComponent
  ]
})
export class ExerciseFilterDialogComponent implements OnInit {
  readonly responsiveService = inject(ResponsiveService);
  
  visible = signal<boolean>(true);
  @Input() filterConstants: IExerciseFilterConstants | null = null;
  @Input() currentFilters: IExerciseFilters | null = null;
  @Input() hideTypeFilter: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<IExerciseFilters>();

  // Filter state
  selectedMuscles = signal<string[]>([]);
  selectedCategories = signal<string[]>([]);
  selectedLevels = signal<string[]>([]);
  muscleFilterMode = signal<'AtLeast' | 'Only'>('AtLeast');
  selectedMainEquipment = signal<string | null>(null);
  selectedSupportEquipment = signal<string | null>(null);
  selectedExerciseAccess = signal<string | null>(null);
  selectedPhases = signal<{
    warmup: boolean;
    resistance: boolean;
    cardio: boolean;
    cooldown: boolean;
  }>({
    warmup: false,
    resistance: false,
    cardio: false,
    cooldown: false
  });
  selectedMovementPatterns = signal<string[]>([]);

  // Expanded sections
  expandedSections = signal<Record<string, boolean>>({
    targetedMuscle: false,
    category: false,
    level: false,
    muscleFilterMode: false,
    mainEquipment: false,
    supportEquipment: false,
    exerciseAccess: false,
    phase: false,
    movementPattern: false
  });

  ngOnInit(): void {
    if (this.currentFilters) {
      this.selectedMuscles.set([...this.currentFilters.targetedMuscles]);
      this.selectedCategories.set([...this.currentFilters.categories]);
      this.selectedLevels.set([...this.currentFilters.levels]);
      this.muscleFilterMode.set(this.currentFilters.muscleFilterMode);
      this.selectedMainEquipment.set(this.currentFilters.mainEquipment);
      this.selectedSupportEquipment.set(this.currentFilters.supportEquipment);
      this.selectedExerciseAccess.set(this.currentFilters.exerciseAccess);
      this.selectedMovementPatterns.set([...(this.currentFilters.movementPatterns || [])]);
      
      // Set phases based on phases array
      const phases = this.currentFilters.phases || [];
      this.selectedPhases.set({
        warmup: phases.includes('WarmUp'),
        resistance: phases.includes('ResistanceTraining'),
        cardio: phases.includes('Cardio'),
        cooldown: phases.includes('CoolDown')
      });
    }
  }

  toggleSection(section: string): void {
    const current = this.expandedSections();
    this.expandedSections.set({
      ...current,
      [section]: !current[section]
    });
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections()[section] ?? false;
  }

  // Muscle methods
  isMuscleSelected(muscle: string): boolean {
    return this.selectedMuscles().includes(muscle);
  }

  toggleMuscle(muscle: string): void {
    const current = this.selectedMuscles();
    if (current.includes(muscle)) {
      this.selectedMuscles.set(current.filter(m => m !== muscle));
    } else {
      this.selectedMuscles.set([...current, muscle]);
    }
  }

  // Category methods
  isCategorySelected(category: string): boolean {
    return this.selectedCategories().includes(category);
  }

  toggleCategory(category: string): void {
    const current = this.selectedCategories();
    if (current.includes(category)) {
      this.selectedCategories.set(current.filter(c => c !== category));
    } else {
      this.selectedCategories.set([...current, category]);
    }
  }

  // Level methods
  isLevelSelected(level: string): boolean {
    return this.selectedLevels().includes(level);
  }

  toggleLevel(level: string): void {
    const current = this.selectedLevels();
    if (current.includes(level)) {
      this.selectedLevels.set(current.filter(l => l !== level));
    } else {
      this.selectedLevels.set([...current, level]);
    }
  }

  // Movement Pattern methods (multiple selection with AND logic)
  isMovementPatternSelected(pattern: string): boolean {
    return this.selectedMovementPatterns().includes(pattern);
  }

  toggleMovementPattern(pattern: string): void {
    const current = this.selectedMovementPatterns();
    if (current.includes(pattern)) {
      this.selectedMovementPatterns.set(current.filter(p => p !== pattern));
    } else {
      this.selectedMovementPatterns.set([...current, pattern]);
    }
  }

  // Equipment methods - update to support deselection
  toggleMainEquipment(equipment: string): void {
    const current = this.selectedMainEquipment();
    if (current === equipment) {
      this.selectedMainEquipment.set(null);
    } else {
      this.selectedMainEquipment.set(equipment);
    }
  }

  toggleSupportEquipment(equipment: string): void {
    const current = this.selectedSupportEquipment();
    if (current === equipment) {
      this.selectedSupportEquipment.set(null);
    } else {
      this.selectedSupportEquipment.set(equipment);
    }
  }

  toggleExerciseAccess(access: string): void {
    const current = this.selectedExerciseAccess();
    if (current === access) {
      this.selectedExerciseAccess.set(null);
    } else {
      this.selectedExerciseAccess.set(access);
    }
  }

  // Get flat muscle list
  getFlatMuscleList(): string[] {
    if (!this.filterConstants) return [];
    const muscles: string[] = [];
    Object.values(this.filterConstants.Muscles).forEach(group => {
      muscles.push(...group);
    });
    return muscles;
  }

  // Get muscle groups
  getMuscleGroups(): { groupName: string; muscles: string[] }[] {
    if (!this.filterConstants) return [];
    return Object.entries(this.filterConstants.Muscles).map(([groupName, muscles]) => ({
      groupName,
      muscles
    }));
  }

  // Get exercise access options as array
  getExerciseAccessOptions(): { key: string; label: string }[] {
    if (!this.filterConstants) return [];
    return Object.entries(this.filterConstants.ExerciseAccessFilter).map(([key, label]) => ({
      key,
      label
    }));
  }

  getMovementPatterns(): string[] {
    return this.filterConstants?.MovementPattern ?? [];
  }

  // Phase toggle method
  togglePhase(phase: 'warmup' | 'resistance' | 'cardio' | 'cooldown'): void {
    const current = this.selectedPhases();
    this.selectedPhases.set({
      ...current,
      [phase]: !current[phase]
    });
  }

  // Clear all filters
  onClearAll(): void {
    this.selectedMuscles.set([]);
    this.selectedCategories.set([]);
    this.selectedLevels.set([]);
    this.muscleFilterMode.set('AtLeast');
    this.selectedMainEquipment.set(null);
    this.selectedSupportEquipment.set(null);
    this.selectedExerciseAccess.set(null);
    this.selectedMovementPatterns.set([]);
    this.selectedPhases.set({
      warmup: false,
      resistance: false,
      cardio: false,
      cooldown: false
    });
  }

  // Apply filters
  onApply(): void {
    const phases = this.selectedPhases();
    const selectedPhasesList: ('WarmUp' | 'ResistanceTraining' | 'Cardio' | 'CoolDown')[] = [];
    
    if (phases.warmup) selectedPhasesList.push('WarmUp');
    if (phases.resistance) selectedPhasesList.push('ResistanceTraining');
    if (phases.cardio) selectedPhasesList.push('Cardio');
    if (phases.cooldown) selectedPhasesList.push('CoolDown');
    
    const filters: IExerciseFilters = {
      targetedMuscles: this.selectedMuscles(),
      categories: this.selectedCategories(),
      levels: this.selectedLevels(),
      muscleFilterMode: this.muscleFilterMode(),
      mainEquipment: this.selectedMainEquipment(),
      supportEquipment: this.selectedSupportEquipment(),
      exerciseAccess: this.selectedExerciseAccess(),
      phases: selectedPhasesList,
      movementPatterns: this.selectedMovementPatterns()
    };
    this.applyFilters.emit(filters);
  }

  // Close dialog
  onClose(): void {
    // For mobile, set visible to false and let the drawer handle the animation
    if (this.responsiveService.isMobileView()) {
      this.visible.set(false);
        this.close.emit();
    } else {
      this.close.emit();
    }
  }

  // Computed signals for active filter checks
  activeFilterCount = computed(() => {
    let count = 0;
    const phases = this.selectedPhases();
    count += this.selectedMuscles().length;
    count += this.selectedCategories().length;
    count += this.selectedLevels().length;
    count += this.selectedMovementPatterns().length;
    if (this.selectedMainEquipment()) count++;
    if (this.selectedSupportEquipment()) count++;
    if (this.selectedExerciseAccess()) count++;
    if (phases.warmup) count++;
    if (phases.resistance) count++;
    if (phases.cardio) count++;
    if (phases.cooldown) count++;
    return count;
  });

  hasMuscleFilters = computed(() => {
    return this.selectedMuscles().length > 0;
  });

  hasCategoryFilters = computed(() => {
    return this.selectedCategories().length > 0;
  });

  hasLevelFilters = computed(() => {
    return this.selectedLevels().length > 0;
  });

  hasMainEquipmentFilter = computed(() => {
    return this.selectedMainEquipment() !== null;
  });

  hasSupportEquipmentFilter = computed(() => {
    return this.selectedSupportEquipment() !== null;
  });

  hasExerciseAccessFilter = computed(() => {
    return this.selectedExerciseAccess() !== null;
  });

  hasPhaseFilter = computed(() => {
    const phases = this.selectedPhases();
    return phases.warmup || phases.resistance || phases.cardio || phases.cooldown;
  });

  hasMovementPatternFilters = computed(() => {
    return this.selectedMovementPatterns().length > 0;
  });
}

